import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from '@material-ui/core/Checkbox';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import './HeaderRow.scss';

const HeaderRow = (props) => {
  const {
    configuration = {},
    columns,
    numChecked,
    checkableCount,
    canCheck,
    canDelete,
    canEdit,
    editedRow,
    canCreate,
    sortedColumn,
    sortOrder,
    tableHandler,
    HeaderButtons,
    RowButtons,
  } = props;
  const createSortHandler = column => () => {
    tableHandler.onSort(column);
  };
  const getColKey = (col, i) => `${col.name}${i}`;
  const AddRowIcon = configuration.add && configuration.add.icon
      && tableHandler.getIcon(configuration.add.icon)
      || AddIcon;
  const addTooltip = configuration.add && configuration.add.tooltip
          || tableHandler.getString('addNewRow', 'Add new row');
  const DeleteRowIcon = configuration.delete && configuration.delete.icon
      && tableHandler.getIcon(configuration.delete.icon)
      || DeleteIcon;
  const deleteTooltip = configuration.delete && configuration.delete.tooltip
          || tableHandler.getString('deleteRows', `Delete checked ${tableHandler.getString('rowOrRows', 'row(s)')}`);

  return (
    <TableRow className="tuc-table-header-row">
      {canCheck && (
        <TableCell padding="checkbox" key="HeaderSelect" className="tuc-table-cell-checkbox">
          <Checkbox
            indeterminate={numChecked > 0 && numChecked < checkableCount}
            checked={numChecked === checkableCount}
            onChange={tableHandler.onCheckAllClick}
          />
        </TableCell>
      )}
      {columns.map((col, i) => {
        const { canSort = true } = col;
        const numeric = col.type === 'integer' || col.type === 'number';
        return (
          <TableCell
            key={getColKey(col, i)}
            align={col.align || (numeric ? 'right' : 'left')}
            className={classnames(numeric && 'tuc-table-cell-numeric', col.className)}
          >
            {canSort && (
              <Tooltip
                title={col.description || col.label}
                placement={numeric ? 'bottom-end' : 'bottom-start'}
                enterDelay={300}
              >
                <TableSortLabel
                  active={sortedColumn === col}
                  direction={sortOrder}
                  onClick={createSortHandler(col)}
                >
                  {col.label}
                </TableSortLabel>
              </Tooltip>
            )}
            {!canSort && col.description && (
              <Tooltip
                title={col.description || 'sort'}
                placement={numeric ? 'bottom-end' : 'bottom-start'}
                enterDelay={300}
              >
                <span>{col.label}</span>
              </Tooltip>
            )}
            {!canSort && !col.description && (
              <span>{col.label}</span>
            )}
          </TableCell>
        );
      })}
      {(canDelete || canEdit || canCreate || HeaderButtons || RowButtons) && (
        <TableCell key="edit" padding="checkbox" className="tuc-table-edit-column">
          <div className="tuc-button-row">
            {canCreate && !editedRow && (
              <Tooltip title={addTooltip}>
                <Fab
                  color="secondary"
                  aria-label="Add"
                  onClick={tableHandler.onCreate}
                  id="grb-table-header-create-button"
                >
                  <AddRowIcon />
                </Fab>
              </Tooltip>
            )}
            {canDelete && !editedRow && (
              <Tooltip title={deleteTooltip}>
                <div>
                  {/* div to avoid material-ui warning about having a tooltip on an inactive button */}
                  <Fab
                    color="primary"
                    aria-label="Delete"
                    onClick={tableHandler.onDelete}
                    disabled={!numChecked}
                    id="grb-table-header-delete-button"
                  >
                    <DeleteRowIcon />
                  </Fab>
                </div>
              </Tooltip>
            )}
            {HeaderButtons && (
              <HeaderButtons {...props} />
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};

HeaderRow.propTypes = {
  configuration: PropTypes.shape({}),
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  numChecked: PropTypes.number.isRequired,
  checkableCount: PropTypes.number.isRequired,
  canCheck: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  editedRow: PropTypes.shape({}),
  canCreate: PropTypes.bool.isRequired,
  sortedColumn: PropTypes.shape({}),
  sortOrder: PropTypes.string,
  tableHandler: PropTypes.shape({
    onSort: PropTypes.func,
    getString: PropTypes.func,
    getIcon: PropTypes.func,
    onCheckAllClick: PropTypes.func,
    onCreate: PropTypes.func,
    onDelete: PropTypes.func,
  }).isRequired,
  HeaderButtons: PropTypes.func,
  RowButtons: PropTypes.func,
};

HeaderRow.defaultProps = {
  configuration: undefined,
  editedRow: undefined,
  sortedColumn: undefined,
  sortOrder: undefined,
  HeaderButtons: undefined,
  RowButtons: undefined,
};

export default HeaderRow;
